import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Typography,
    Box,
    Button,
    IconButton,
    Tooltip,
    Grid,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import apiCall from '../../../../../../app/API/apiService';
import apiEndpoints from '../../../../../../app/API/apiEndpoints';
import { toasty } from '../../../../../../Helper';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ViewRoundsIcon from '../../../../../assets/svgComponent/ViewRounds.svg';

const CandidateJobOpenings = () => {
    const [candidate, setCandidate] = useState(null);
    const [jobOpenings, setJobOpenings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [jobOpeningsError, setJobOpeningsError] = useState(null);
    const [selectedJobId, setSelectedJobId] = useState(null);
    
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // First check URL params
        const urlParams = new URLSearchParams(window.location.search);
        const candidateIdFromUrl = urlParams.get("candidate_id");
        
        // If we have an ID in the URL, save it to localStorage
        if (candidateIdFromUrl) {
            localStorage.setItem('currentCandidateId', candidateIdFromUrl);
            fetchCandidateData(candidateIdFromUrl);
            fetchJobOpenings(candidateIdFromUrl);
        } else {
            // If not in URL, try to get from localStorage
            const storedCandidateId = localStorage.getItem('currentCandidateId');
            if (storedCandidateId) {
                // Update URL with stored ID
                navigate(`/candidate-job-openings?candidate_id=${storedCandidateId}`, { replace: true });
                fetchCandidateData(storedCandidateId);
                fetchJobOpenings(storedCandidateId);
            } else {
                // No ID in URL or localStorage, redirect to list
                navigate('/candidate-list');
            }
        }

        // Cleanup function to remove stored ID when component unmounts
        return () => {
            localStorage.removeItem('currentCandidateId');
        };
    }, [navigate]);

    const fetchCandidateData = async (id) => {
        setIsLoading(true);
        try {
            const response = await apiCall("get", `${apiEndpoints.CANDIDATE_LIST}/${id}`);
            setCandidate(response);
            setError(null);
        } catch (error) {
            console.error("Error fetching candidate data:", error);
            setError("Failed to fetch candidate details");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchJobOpenings = async (candidateId) => {
        try {
            const response = await apiCall(
                "get", 
                `${apiEndpoints.CANDIDATE_LIST}/${candidateId}/jobopenings`
            );
            setJobOpenings(response || []);
            setJobOpeningsError(null);
        } catch (error) {
            console.error("Error fetching job openings:", error);
            setJobOpeningsError("Failed to fetch job openings");
            setJobOpenings([]);
        }
    };

    const handleBackToList = () => {
        navigate('/candidate-list');
    };

    const handleCopyText = (text) => {
        navigator.clipboard.writeText(text);
        toasty('Copied to clipboard!', 'success');
    };

    const handleViewRounds = (jobId) => {
        const candidateId = localStorage.getItem('currentCandidateId');
        if (candidateId) {
            navigate(`/candidate-interviews?candidate_id=${candidateId}&job_opening_id=${jobId}`);
        }
    };

    // Update these styles
    const strongStyle = {
        minWidth: '120px',
        height: '20px',
        fontFamily: 'Inter',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '16px',
        letterSpacing: '-0.1px',
        textAlign: 'left',
        textUnderlinePosition: 'from-font',
        textDecorationSkipInk: 'none',
        color: 'var(--Gray-700, rgba(78, 78, 86, 1))',
        marginBottom: '2px'
    };

    const spanStyle = {
        minWidth: '150px',
        height: '25px',
        fontFamily: 'Inter',
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '-0.2px',
        textAlign: 'left',
        textUnderlinePosition: 'from-font',
        textDecorationSkipInk: 'none',
        color: 'var(--Gray-900, rgba(58, 58, 64, 1))',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        marginBottom: '8px'
    };

    const tableCellStyle = {
        width: '270px',
        minHeight: '45px',
        padding: '12px 16px',
        gap: '4px',
        borderBottom: '0.5px solid rgba(0, 0, 0, 0.12)',
        verticalAlign: 'top'
    };

    const cellContentStyle = {
        display: "flex",
        flexDirection: "column",
        gap: '2px',
        marginBottom: '2px'
    };

    // Add a specific style for the header row
    const headerCellStyle = {
        ...tableCellStyle,
        padding: '8px 16px',
    };

    // Add a specific style for education qualification rows
    const educationCellStyle = {
        ...tableCellStyle,
        minHeight: '40px',
    };

    if (isLoading) {
        return <Typography>Loading...</Typography>;
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Button 
                    variant="contained"
                    onClick={handleBackToList} 
                    sx={{ 
                        mb: 2,
                        backgroundColor: '#1976d2',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: '#1565c0',
                        }
                    }}
                >
                    Back to Candidates List
                </Button>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <div>
            <TableContainer component={Paper}>
                <Table aria-label="candidate details table" size="small">
                    <TableBody>
                        {/* Header Row */}
                        <TableRow>
                            <TableCell colSpan={6} style={headerCellStyle}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ width: "100%" }}
                                >
                                    <Typography 
                                        align="left" 
                                        variant="h6"
                                        sx={{ 
                                            fontFamily: 'Inter',
                                            color: 'var(--Gray-700, rgba(78, 78, 86, 1))',
                                            fontSize: '16px',
                                            marginBottom: '20px'
                                        }}
                                    >
                                        Candidate Details
                                    </Typography>
                                    <Button 
                                        variant="contained"
                                        onClick={handleBackToList}
                                        sx={{ 
                                            height: '24px',
                                            fontFamily: 'Inter',
                                            textTransform: 'none',
                                            backgroundColor: '#1976d2',
                                            fontSize: '12px',
                                            '&:hover': {
                                                backgroundColor: '#1565c0',
                                            }
                                        }}
                                    >
                                        Back to Candidates List
                                    </Button>
                                </Box>
                            </TableCell>
                        </TableRow>

                        {/* Basic Information */}
                        <TableRow>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>Name:</strong>
                                    <span style={spanStyle}>{candidate?.candidate_name || "N/A"}</span>
                                </div>
                            </TableCell>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>Email:</strong>
                                    <span style={spanStyle}>{candidate?.candidate_email || "N/A"}</span>
                                </div>
                            </TableCell>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>Phone:</strong>
                                    <span style={spanStyle}>{candidate?.candidate_phone || "N/A"}</span>
                                </div>
                            </TableCell>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>Location:</strong>
                                    <span style={spanStyle}>{candidate?.current_location || "N/A"}</span>
                                </div>
                            </TableCell>
                        </TableRow>

                        {/* Professional Information */}
                        <TableRow>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>Current CTC:</strong>
                                    <span style={spanStyle}>
                                        ₹{candidate?.current_ctc ? candidate.current_ctc.toLocaleString() : "N/A"}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>Expected CTC:</strong>
                                    <span style={spanStyle}>
                                        ₹{candidate?.expected_ctc ? candidate.expected_ctc.toLocaleString() : "N/A"}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>Notice Period:</strong>
                                    <span style={spanStyle}>{candidate?.notice_period || "N/A"}</span>
                                </div>
                            </TableCell>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>Experience:</strong>
                                    <span style={spanStyle}>{candidate?.experience || "N/A"}</span>
                                </div>
                            </TableCell>
                        </TableRow>

                        {/* AI Score and AI Summary */}
                        <TableRow>
                            <TableCell style={tableCellStyle}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>AI Score:</strong>
                                    <span style={spanStyle}>{candidate.ai_score ? `${candidate.ai_score}` : '0.0'}</span>
                                </div>
                            </TableCell>
                            <TableCell style={tableCellStyle} colSpan={3}>
                                <div style={cellContentStyle}>
                                    <strong style={strongStyle}>AI Summary:</strong> 
                                    <span style={{
                                        ...spanStyle,
                                        height: 'auto',
                                        maxHeight: '60px',
                                        overflowY: 'auto'
                                    }}>{candidate.ai_summary || "N/A"}</span>
                                </div>
                            </TableCell>
                        </TableRow>

                        {/* Education Qualifications */}
                        {candidate.education_qualification?.length > 0 && (
                            <>
                                <TableRow>
                                    <TableCell style={educationCellStyle} colSpan={6}>
                                        <Typography 
                                            variant="h6" 
                                            style={{
                                                fontFamily:'Inter',
                                                color: 'var(--Gray-700, rgba(78, 78, 86, 1))',
                                                fontSize: '14px',
                                                marginBottom: '4px'
                                            }}
                                        > 
                                            Education Qualifications:
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                {candidate.education_qualification.map((edu, index) => (
                                    <TableRow key={index}>
                                        <TableCell style={educationCellStyle} colSpan={2}>
                                            <div style={cellContentStyle}>
                                                <strong style={strongStyle}>Degree:</strong>
                                                <span style={spanStyle}>{edu.degree_name || "N/A"}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell style={educationCellStyle} colSpan={2}>
                                            <div style={cellContentStyle}>
                                                <strong style={strongStyle}>Institution:</strong>
                                                <span style={spanStyle}>{edu.institution_name || "N/A"}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell style={educationCellStyle} colSpan={2}>
                                            <div style={cellContentStyle}>
                                                <strong style={strongStyle}>Year:</strong>
                                                <span style={spanStyle}>{edu.year || "N/A"}</span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </>
                        )}

                        {/* Skills Row */}
                        {candidate.skills?.length > 0 && (
                            <>
                                <TableRow>
                                    <TableCell style={tableCellStyle} colSpan={6}>
                                        <Typography style={{fontFamily:'Inter', variant:'h6'}}>Skills</Typography>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell style={tableCellStyle} colSpan={6}>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {candidate.skills.map((skill, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        border: "1px dashed gray",
                                                        color: "rgba(0,0,0,0.8)",
                                                        padding: "6px 20px",
                                                        borderRadius: "17px",
                                                        transition:
                                                            "background-color 0.3s, border-color 0.3s",
                                                        fontFamily: "Inter",
                                                    }}
                                                >
                                                    {skill}
                                                </div>
                                            ))}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </>
                        )}

                        {/* Job Openings Section */}
                        <TableRow>
                            <TableCell colSpan={6} style={tableCellStyle}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mb: 2,
                                        fontFamily: 'Inter',
                                        fontWeight: 500,
                                        color: 'var(--Gray-700, rgba(78, 78, 86, 1))',
                                        padding: '0.5rem',
                                        borderRadius: '0.25rem'
                                    }}
                                >
                                    Applied Job Openings
                                </Typography>
                                {jobOpenings.map((job) => (
                                    <Box
                                        key={job.id}
                                        sx={{
                                            mb: 2,
                                            p: {
                                                xs: 1,
                                                sm: 1.5,
                                                md: 2
                                            },
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            borderRadius: '8px',
                                            '&:hover': { 
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                            }
                                        }}
                                    >
                                        <Grid 
                                            container 
                                            spacing={{ xs: 2, sm: 4, md: 6 }}
                                            alignItems="flex-start"
                                        >
                                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                                <div style={cellContentStyle}>
                                                    <strong style={strongStyle}>Job Opening ID</strong>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center',
                                                        position: 'relative'
                                                    }}>
                                                        <span style={spanStyle}>{job.id ? job.id.slice(-10) : "N/A"}</span>
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                right: '-2rem',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)'
                                                            }}
                                                            onClick={() => handleCopyText(job.id)}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                                <div style={cellContentStyle}>
                                                    <strong style={strongStyle}>Job Opening Name</strong>
                                                    <span style={spanStyle}>{job.jobopening_name || "N/A"}</span>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                                <div style={cellContentStyle}>
                                                    <strong style={strongStyle}>Role</strong>
                                                    <span style={spanStyle}>{job.role_name || "N/A"}</span>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                                <div style={cellContentStyle}>
                                                    <strong style={strongStyle}>Department</strong>
                                                    <span style={spanStyle}>{job.department_name || "N/A"}</span>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                                <div style={cellContentStyle}>
                                                    <strong style={strongStyle}>Subdepartment</strong>
                                                    <span style={spanStyle}>{job.subdepartment_name || "N/A"}</span>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                                <div style={cellContentStyle}>
                                                    <strong style={strongStyle}>Rounds Given</strong>
                                                    <IconButton
                                                        onClick={() => handleViewRounds(job.id)}
                                                        sx={{
                                                            padding: 0,
                                                            width: 'auto',
                                                            height: 'auto',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            }
                                                        }}
                                                    >
                                                        <img 
                                                            src={ViewRoundsIcon} 
                                                            alt="View Rounds"
                                                            style={{ 
                                                                width: '100%',
                                                                maxWidth: '120px',
                                                                height: 'auto'
                                                            }} 
                                                        />
                                                    </IconButton>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default CandidateJobOpenings;